import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function JobEditPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type_contrat: '',
    budget: '',
    ville: '',
    pays: '',
    filiale: ''
  });
  const [preview, setPreview] = useState(false);
  const [message, setMessage] = useState('');

  const subsidiaries = ['MFG Technologies', 'Asking Group', 'Gem', 'Growth Energy'];
  const countries = ['France', 'Canada', 'Sénégal', 'Maroc', 'USA', 'UK'];

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_DOMAINE}/api/jobs`);
      const foundJob = res.data.find(j => j.id === parseInt(jobId));
      if (foundJob) {
        setJob(foundJob);
        setFormData({
          title: foundJob.title || '',
          description: foundJob.description || '',
          type_contrat: foundJob.type_contrat || '',
          budget: foundJob.budget || '',
          ville: foundJob.ville || '',
          pays: foundJob.pays || '',
          filiale: foundJob.filiale || ''
        });
      }
    } catch (err) {
      console.error('Erreur récupération offre:', err);
      setMessage('❌ Erreur lors de la récupération de l\'offre');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        budget: parseInt(formData.budget) || 0,
      };

      await axios.put(`${process.env.REACT_APP_DOMAINE}/api/jobs/${jobId}`, payload);
      setMessage('✅ Offre modifiée avec succès');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      console.error('Erreur mise à jour:', err.response?.data || err.message);
      setMessage('❌ Erreur lors de la modification');
    }
  };

  // Simple markdown-like rendering
  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .split('\n')
      .map((line, i) => {
        // Headers
        if (line.startsWith('# ')) return `<h1 key="${i}">${line.substring(2)}</h1>`;
        if (line.startsWith('## ')) return `<h2 key="${i}">${line.substring(3)}</h2>`;
        if (line.startsWith('### ')) return `<h3 key="${i}">${line.substring(4)}</h3>`;
        // Bold
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        // Italic
        line = line.replace(/\*(.*?)\*/g, '<em>$1</em>');
        // Lists
        if (line.startsWith('- ')) return `<li key="${i}">${line.substring(2)}</li>`;
        // Paragraphs
        return line ? `<p key="${i}">${line}</p>` : '<br key="${i}"/>';
      })
      .join('');
  };

  if (!job) {
    return <div className="container mt-4"><p>Chargement...</p></div>;
  }

  return (
    <div className="container mt-4">
      <div className="card p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3>📝 Modifier l'offre : {job.title}</h3>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            ← Retour
          </button>
        </div>

        {message && <div className="alert alert-info">{message}</div>}

        {/* Toggle Preview/Edit */}
        <div className="btn-group mb-3" role="group">
          <button
            type="button"
            className={`btn ${!preview ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPreview(false)}
          >
            ✏️ Édition
          </button>
          <button
            type="button"
            className={`btn ${preview ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPreview(true)}
          >
            👁️ Aperçu
          </button>
        </div>

        {!preview ? (
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Titre du poste</label>
                <input
                  className="form-control"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Type de contrat</label>
                <select
                  className="form-select"
                  name="type_contrat"
                  value={formData.type_contrat}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="CDI">Poste temps plein</option>
                  <option value="CDD">Poste temps partiel</option>
                  <option value="Contractuel">Poste contractuel</option>
                  <option value="Stage">Stage</option>
                  <option value="Alternance">Alternance</option>
                  <option value="Freelance">Freelance</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Description (Markdown supporté)</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={10}
                placeholder="# Titre&#10;## Sous-titre&#10;**Gras** *Italique*&#10;- Item 1&#10;- Item 2"
              />
              <small className="form-text text-muted">
                Syntaxe Markdown : # Titre, ## Sous-titre, **gras**, *italique*, - liste
              </small>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Budget estimé (€)</label>
                <input
                  type="number"
                  className="form-control"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Ville</label>
                <input
                  className="form-control"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Pays</label>
                <select
                  className="form-select"
                  name="pays"
                  value={formData.pays}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Filiale</label>
              <select
                className="form-select"
                name="filiale"
                value={formData.filiale}
                onChange={handleChange}
                required
              >
                <option value="">-- Sélectionner --</option>
                {subsidiaries.map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>

            <button type="submit" className="btn btn-success me-2">
              ✅ Enregistrer les modifications
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              Annuler
            </button>
          </form>
        ) : (
          <div className="preview-container p-4 border rounded">
            <h4>{formData.title}</h4>
            <div className="mb-3">
              <span className="badge bg-primary me-2">{formData.type_contrat}</span>
              <span className="badge bg-success">{formData.budget}€</span>
            </div>
            <div
              className="markdown-preview"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(formData.description) }}
            />
            <hr />
            <p><strong>Lieu:</strong> {formData.ville}, {formData.pays}</p>
            <p><strong>Filiale:</strong> {formData.filiale}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default JobEditPage;
